/**
 * SubmissionsSection — Tab 3b (Offertanfragen: erstellen · versenden · erinnern · Status · Frist;
 * P2 Placement Slice 8). Query = usePlacementSubmissions; every mutation goes through the hook and
 * invalidates the server state. The Versenden dialog's open-state can be lifted to the screen (the
 * primary CTA) via `versendenOpen`/`onVersendenClose`, else it is section-local.
 */

import { useState } from 'react';
import { Button } from '../../components/ui/Button';
import { CountBadge } from '../../components/ui/CountBadge';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import {
  SUBMISSION_KEINE_AUSWAHL_HINWEIS,
  SUBMISSION_MAILBOX_HINWEIS,
  VERSICHERER_VORGABEN_OFFEN_HINWEIS,
} from '@brokly/shared';
import type { SubmissionEintrag } from '@brokly/shared';
import { SubmissionRow } from './SubmissionRow';
import { SubmissionVersendenDialog } from './SubmissionVersendenDialog';
import { SubmissionErinnernDialog } from './SubmissionErinnernDialog';
import { SubmissionGrundDialog } from './SubmissionGrundDialog';
import { SubmissionUnterlagenDialog } from './SubmissionUnterlagenDialog';
import {
  usePlacementSubmissions,
  useErstellenSubmissions,
  useVersendenSubmissions,
  useErinnernSubmission,
  useVersandAufloesenSubmission,
  useNichtOfferiertSubmission,
  useInaktivSubmission,
  usePatchSubmissionFrist,
} from './usePlacementSubmissions';
import { apiClient } from '../../lib/api-client';
import { isLiveData } from '../../lib/data-mode';
import type { SubmissionUnterlagenVorschauInfo } from '@brokly/shared';

type DialogState =
  | { kind: 'none' }
  | { kind: 'erinnern'; item: SubmissionEintrag }
  | { kind: 'nicht_offeriert'; item: SubmissionEintrag }
  | { kind: 'inaktiv'; item: SubmissionEintrag }
  | { kind: 'unterlagen'; item: SubmissionEintrag; unterlagen: SubmissionUnterlagenVorschauInfo[] };

export function SubmissionsSection({
  placementId,
  canWrite,
  versendenOpen: versendenOpenProp,
  onVersendenClose,
}: {
  placementId: string;
  canWrite: boolean;
  versendenOpen?: boolean;
  onVersendenClose?: () => void;
}) {
  const { can } = useEffectivePermissions();
  const canSend = canWrite && can('communication.send');

  const query = usePlacementSubmissions(placementId);
  const erstellen = useErstellenSubmissions(placementId);
  const versenden = useVersendenSubmissions(placementId);
  const erinnern = useErinnernSubmission(placementId);
  const versandAufloesen = useVersandAufloesenSubmission(placementId);
  const nichtOfferiert = useNichtOfferiertSubmission(placementId);
  const inaktiv = useInaktivSubmission(placementId);
  const patchFrist = usePatchSubmissionFrist(placementId);

  const [localVersendenOpen, setLocalVersendenOpen] = useState(false);
  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });
  const [error, setError] = useState<string | null>(null);

  const versendenOpen = versendenOpenProp ?? localVersendenOpen;
  const closeVersenden = () => {
    setLocalVersendenOpen(false);
    onVersendenClose?.();
  };

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Lade Offertanfragen…</p>;
  if (query.isError || !query.data) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="placement-submissions-error">
        Offertanfragen konnten nicht geladen werden.
      </p>
    );
  }
  const data = query.data;
  const neuItems = data.items.filter((i) => i.status === 'neu');

  function mapErr(e: unknown): string {
    const code = e instanceof Error ? e.message : String(e);
    return code || 'Aktion fehlgeschlagen.';
  }

  async function doErstellen() {
    setError(null);
    try {
      await erstellen.mutateAsync();
    } catch (e) {
      setError(mapErr(e));
    }
  }

  return (
    <section className="flex flex-col gap-3" data-testid="placement-submissions-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold">Offertanfragen</h3>
          <CountBadge count={data.neu} /> <span className="text-xs text-muted-foreground">neu</span>
          <CountBadge count={data.versandt} /> <span className="text-xs text-muted-foreground">versandt</span>
          <CountBadge count={data.erinnert} /> <span className="text-xs text-muted-foreground">erinnert</span>
          <CountBadge count={data.eingegangen} /> <span className="text-xs text-muted-foreground">eingegangen</span>
          {data.frist_abgelaufen > 0 && (
            <span className="text-xs text-status-warning">{data.frist_abgelaufen} Frist abgelaufen</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {data.aktion_erlaubt && data.erstellbar > 0 && canWrite && (
            <Button onClick={() => void doErstellen()} disabled={erstellen.isPending} data-testid="sub-erstellen">
              Offertanfragen erstellen ({data.erstellbar})
            </Button>
          )}
          {data.aktion_erlaubt && data.neu > 0 && canSend && (
            <Button onClick={() => setLocalVersendenOpen(true)} data-testid="sub-versenden-open">
              Versenden ({data.neu})
            </Button>
          )}
          {data.aktion_erlaubt && data.neu > 0 && canSend && !data.mailbox_verbunden && (
            <span className="text-xs text-muted-foreground" role="note">
              {SUBMISSION_MAILBOX_HINWEIS}
            </span>
          )}
        </div>
      </div>

      {error && <p className="text-xs text-status-danger" data-testid="sub-section-error">{error}</p>}

      {!data.aktion_erlaubt && (
        <p className="rounded-md border border-status-warning-border bg-status-warning-bg p-2 text-xs text-status-warning" role="note">
          {data.sperrgrund === 'vorgaben_nicht_erfasst' ? VERSICHERER_VORGABEN_OFFEN_HINWEIS : 'Die Ausschreibung ist gesperrt.'}
        </p>
      )}

      {data.aktion_erlaubt && data.items.length === 0 && data.erstellbar === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="sub-empty">
          {SUBMISSION_KEINE_AUSWAHL_HINWEIS}
        </p>
      )}

      <div className="flex flex-col gap-2">
        {data.items.map((item) => (
          <SubmissionRow
            key={item.id}
            item={item}
            canWrite={canWrite}
            canSend={canSend}
            aktionErlaubt={data.aktion_erlaubt}
            onErinnern={() => setDialog({ kind: 'erinnern', item })}
            onNichtOfferiert={() => setDialog({ kind: 'nicht_offeriert', item })}
            onInaktiv={() => setDialog({ kind: 'inaktiv', item })}
            onUnterlagen={() => void openUnterlagen(item)}
            onFrist={(frist_date) => void patchFrist.mutateAsync({ sid: item.id, frist_date })}
            onVersandAufloesen={(aktion) => void versandAufloesen.mutate({ sid: item.id, aktion })}
          />
        ))}
      </div>

      <SubmissionVersendenDialog
        open={versendenOpen}
        onClose={closeVersenden}
        neuItems={neuItems}
        fristDefault={data.frist_default_date}
        mailboxVerbunden={data.mailbox_verbunden}
        pending={versenden.isPending}
        onVersenden={(submission_ids, frist_date) => versenden.mutateAsync({ submission_ids, frist_date })}
      />

      {dialog.kind === 'erinnern' && (
        <SubmissionErinnernDialog
          open
          onClose={() => setDialog({ kind: 'none' })}
          carrierName={dialog.item.name ?? dialog.item.carrier_uid}
          pending={erinnern.isPending}
          onSubmit={(nachricht) => {
            void erinnern.mutateAsync({ sid: dialog.item.id, body: { nachricht } }).then(() => setDialog({ kind: 'none' }));
          }}
        />
      )}

      {dialog.kind === 'nicht_offeriert' && (
        <SubmissionGrundDialog
          open
          onClose={() => setDialog({ kind: 'none' })}
          title={`${dialog.item.name ?? dialog.item.carrier_uid} - nicht offeriert`}
          grundRequired={false}
          submitLabel="Als «nicht offeriert» markieren"
          pending={nichtOfferiert.isPending}
          onSubmit={(grund) => {
            void nichtOfferiert.mutateAsync({ sid: dialog.item.id, body: { grund } }).then(() => setDialog({ kind: 'none' }));
          }}
        />
      )}

      {dialog.kind === 'inaktiv' && (
        <SubmissionGrundDialog
          open
          onClose={() => setDialog({ kind: 'none' })}
          title={`${dialog.item.name ?? dialog.item.carrier_uid} - inaktiv markieren`}
          grundRequired
          submitLabel="Inaktiv markieren"
          pending={inaktiv.isPending}
          onSubmit={(grund) => {
            void inaktiv.mutateAsync({ sid: dialog.item.id, body: { grund } }).then(() => setDialog({ kind: 'none' }));
          }}
        />
      )}

      {dialog.kind === 'unterlagen' && (
        <SubmissionUnterlagenDialog
          open
          onClose={() => setDialog({ kind: 'none' })}
          placementId={placementId}
          submissionId={dialog.item.id}
          submissionNumber={dialog.item.submission_number}
          unterlagen={dialog.unterlagen}
        />
      )}
    </section>
  );

  async function openUnterlagen(item: SubmissionEintrag) {
    let unterlagen: SubmissionUnterlagenVorschauInfo[] = item.attachments.map((a) => ({
      art: a.art,
      filename: a.filename,
      mime_type: a.mime_type,
      vorschau: false,
      size_bytes: a.size_bytes,
      hidden_reason: null,
    }));
    if (isLiveData()) {
      try {
        const res = await apiClient.placements.getSubmissionUnterlagen(placementId, item.id);
        unterlagen = res.unterlagen;
      } catch {
        /* fall back to the stored attachments already mapped */
      }
    }
    setDialog({ kind: 'unterlagen', item, unterlagen });
  }
}
